/**
 * „Montage eintragen" – derselbe Dialog aus vier Einstiegen
 * (MONTAGEKALENDER.md 3.1).
 *
 * Kalender, rechte Spalte, Mein Tag und Auftrags-Detail öffnen dieses eine
 * Fenster. Vier nachgebaute Formulare wären vier Gelegenheiten, etwas anders
 * zu benennen – und der Termin landet am Ende doch im selben Kalender.
 *
 * Keine Uhrzeit: Montagen sind ganztägig (3.2). „Bis" zählt einschließlich,
 * wie jeder es im Kopf rechnet; die Graph-Konvention mit dem Folgetag löst
 * der Server auf.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import { api } from '../api';
import { Modal } from '../komponenten/Modal';
import { useKora } from '../zustand';
import type { Angebot, MontageEinsatz, Monteur } from '../typen';
import './montagedialog.css';

const zwei = (n: number) => String(n).padStart(2, '0');
const iso = (d: Date) => `${d.getFullYear()}-${zwei(d.getMonth() + 1)}-${zwei(d.getDate())}`;

function plusTage(s: string, n: number): string {
  const d = new Date(Number(s.slice(0, 4)), Number(s.slice(5, 7)) - 1, Number(s.slice(8, 10)));
  d.setDate(d.getDate() + n);
  return iso(d);
}

function kurzDatum(s: string): string {
  const monate = ['Jän.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];
  return `${Number(s.slice(8, 10))}. ${monate[Number(s.slice(5, 7)) - 1]}`;
}

export function MontageDialog({
  einsatz,
  angebotId,
  vorgabeTag,
  monteure: monteureVon,
  alleEinsaetze: einsaetzeVon,
  onSchliessen,
  onFertig,
}: {
  /** Gesetzt heißt verschieben, leer heißt neu eintragen. */
  einsatz: MontageEinsatz | null;
  /** Vorbelegter Auftrag – aus „Montage planen" oder aus dem Auftrags-Detail. */
  angebotId: number | null;
  /** Der im Kalender angeklickte Tag – Von und Bis stehen dann schon (11.3). */
  vorgabeTag?: string;
  /**
   * Monteure und Einsätze – aus dem Kalender durchgereicht.
   *
   * Aus Mein Tag und dem Auftrags-Detail liegen sie nicht vor; dann holt
   * der Dialog sie selbst. So öffnet jeder der vier Einstiege denselben
   * Dialog, ohne dass die Maske davor etwas über Monteure wissen muss.
   */
  monteure?: Monteur[];
  /** Alle Einsätze des gelesenen Zeitraums – für die Doppelbelegung (3.4). */
  alleEinsaetze?: MontageEinsatz[];
  onSchliessen: () => void;
  onFertig: () => void;
}) {
  const { toastZeigen } = useKora();

  /*
   * Ohne mitgegebene Listen holt der Dialog den laufenden und den nächsten
   * Monat – weit genug, um eine Doppelbelegung zu sehen, ohne das halbe
   * Jahr zu laden.
   */
  const eigenerStand = useQuery({
    queryKey: ['montage-dialog'],
    queryFn: () => {
      const heute = iso(new Date());
      return api.hole<{ monteure: Monteur[]; einsaetze: MontageEinsatz[] }>(
        `/montage?von=${plusTage(heute, -40)}&bis=${plusTage(heute, 120)}`,
      );
    },
    enabled: !monteureVon,
  });
  const monteure = monteureVon ?? eigenerStand.data?.monteure ?? [];
  const alleEinsaetze = einsaetzeVon ?? eigenerStand.data?.einsaetze ?? [];

  const [art, setArt] = useState<'montage' | 'service'>(einsatz?.art ?? 'montage');
  const [auftrag, setAuftrag] = useState<number | null>(einsatz?.angebotId ?? angebotId);
  const [titelFrei, setTitelFrei] = useState(einsatz && !einsatz.angebotId ? einsatz.titel : '');
  /* Aus dem Kalender kommt der angeklickte Tag als Vorgabe (11.3). */
  const [von, setVon] = useState(einsatz?.von ?? vorgabeTag ?? '');
  const [bis, setBis] = useState(einsatz?.bis ?? vorgabeTag ?? '');
  const [adresse, setAdresse] = useState(einsatz?.adresse ?? '');
  const [gewaehlt, setGewaehlt] = useState<string[]>(einsatz?.monteure.map((m) => m.id) ?? []);

  /*
   * Die Auftragsliste kommt aus derselben Abfrage wie die Angebotsliste –
   * ein eigener Endpunkt hätte dieselben Daten unter anderem Namen.
   */
  const angebote = useQuery({
    queryKey: ['angebote'],
    queryFn: () => api.hole<Angebot[]>('/angebote'),
  });

  const auftraege = useMemo(
    () =>
      (angebote.data ?? [])
        .filter((a) => a.bestellungen.length > 0 || a.status === 'Auftrag erteilt')
        .sort((x, y) => y.nr - x.nr),
    [angebote.data],
  );
  const gewaehlterAuftrag = auftraege.find((a) => a.id === auftrag) ?? null;

  /*
   * Vorbelegung: Tag nach der Lieferung, zwei Tage (3.2). Erst, wenn der
   * Auftrag steht – vorher weiß niemand, wann geliefert wird.
   */
  useEffect(() => {
    if (einsatz || von) return;
    const liefer = gewaehlterAuftrag?.bestellungen
      .map((b) => b.lieferSortierung)
      .filter((x): x is string => !!x)
      .sort()
      .pop();
    const start = liefer ? plusTage(liefer, 1) : iso(new Date());
    setVon(start);
    setBis(plusTage(start, 1));
  }, [gewaehlterAuftrag, einsatz, von]);

  /* Die Montageadresse kommt aus dem Kunden und bleibt änderbar (3.2). */
  useEffect(() => {
    if (einsatz || adresse || !gewaehlterAuftrag) return;
    const k = gewaehlterAuftrag.kunde;
    setAdresse([k.strasse, [k.plz, k.ort].filter(Boolean).join(' ')].filter(Boolean).join(', '));
  }, [gewaehlterAuftrag, einsatz, adresse]);

  /** Wer im gewählten Zeitraum schon woanders steht (3.4). */
  const konflikte = useMemo(() => {
    if (!von || !bis) return new Map<string, string>();
    const raus = new Map<string, string>();
    for (const e of alleEinsaetze) {
      if (einsatz && e.id === einsatz.id) continue;
      if (e.von > bis || e.bis < von) continue;
      for (const m of e.monteure) {
        if (!raus.has(m.id)) raus.set(m.id, e.kurz || e.titel);
      }
    }
    return raus;
  }, [alleEinsaetze, von, bis, einsatz]);

  const belegte = gewaehlt.filter((id) => konflikte.has(id));

  const speichern = useMutation({
    mutationFn: () => {
      const koerper = {
        art,
        angebotId: art === 'service' && !auftrag ? null : auftrag,
        titelFrei: art === 'service' && !auftrag ? titelFrei : '',
        von,
        bis,
        adresse,
        monteurIds: gewaehlt,
      };
      return einsatz
        ? api.aendere<{ toast: string }>(`/montage/${einsatz.id}`, koerper)
        : api.sende<{ toast: string }>('/montage', koerper);
    },
    onSuccess: (a) => {
      toastZeigen(a.toast);
      onFertig();
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Speichern fehlgeschlagen.'),
  });

  /*
   * Löschen wohnt jetzt hier.
   *
   * Es hing an der Tageskarte rechts, und die ist weg – einen Termin sieht
   * man sich in Outlook an. Die Handlung gehört ohnehin dorthin, wo der
   * Termin bearbeitet wird, und nicht in eine zweite Liste daneben.
   */
  const loeschen = useMutation({
    mutationFn: () => api.loesche<{ toast: string }>(`/montage/${einsatz!.id}`),
    onSuccess: (a) => {
      toastZeigen(a.toast);
      onFertig();
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Löschen nicht möglich.'),
  });

  const gewerk = gewaehlterAuftrag?.prefix ?? '';
  const vorschauTitel =
    art === 'service'
      ? titelFrei.trim() ||
        ['Service', gewaehlterAuftrag?.kunde.name, gewaehlterAuftrag?.kunde.ort].filter(Boolean).join(' · ')
      : ['Montage' + (gewerk ? ` ${gewerk}` : ''), gewaehlterAuftrag?.kunde.name, gewaehlterAuftrag?.kunde.ort]
          .filter(Boolean)
          .join(' · ');

  const bereit = gewaehlt.length > 0 && !!von && !!bis && bis >= von && (art === 'service' || !!auftrag);

  return (
    <Modal
      titel={einsatz ? 'Montage verschieben' : 'Montage eintragen'}
      untertitel={'wird als Termin in „MONTAGE - Termine" eingetragen'}
      onSchliessen={onSchliessen}
      breite={560}
      zeigeSchliessen
      fuss={
        <div className="md-fuss">
          {einsatz ? (
            <button
              type="button"
              className="md-weg"
              disabled={loeschen.isPending}
              onClick={() => {
                if (window.confirm(`„${einsatz.titel}" löschen? Der Termin verschwindet auch aus Outlook.`)) {
                  loeschen.mutate();
                }
              }}
            >
              {loeschen.isPending ? 'Wird gelöscht …' : 'Termin löschen'}
            </button>
          ) : (
            <span className="md-fussnote">Setzt am Auftrag „Montage terminiert".</span>
          )}
          <div className="md-knoepfe">
            <button type="button" className="md-sek" onClick={onSchliessen}>
              Abbrechen
            </button>
            <button
              type="button"
              className={`md-primaer ${bereit ? '' : 'aus'}`}
              disabled={!bereit || speichern.isPending}
              onClick={() => speichern.mutate()}
            >
              {speichern.isPending
                ? 'Wird gespeichert …'
                : einsatz
                  ? 'Verschieben & Outlook aktualisieren'
                  : 'Eintragen & nach Outlook'}
            </button>
          </div>
        </div>
      }
    >
      <div className="md">
        <div className="md-arten">
          {(['montage', 'service'] as const).map((a) => (
            <button key={a} type="button" className={`md-art ${art === a ? 'aktiv' : ''}`} onClick={() => setArt(a)}>
              {a === 'montage' ? 'Montage' : 'Service'}
            </button>
          ))}
        </div>

        <label className="md-feld">
          <span className="md-label">Auftrag{art === 'service' ? ' (freiwillig)' : ''}</span>
          <select
            value={auftrag ?? ''}
            onChange={(e) => setAuftrag(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">{art === 'service' ? 'ohne Auftrag' : '— bitte wählen —'}</option>
            {auftraege.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>

        {art === 'service' && !auftrag && (
          <label className="md-feld">
            <span className="md-label">Titel</span>
            <input
              value={titelFrei}
              placeholder="Service · Baldauf · Markt Allhau"
              onChange={(e) => setTitelFrei(e.target.value)}
            />
          </label>
        )}

        <div className="md-zwei">
          <label className="md-feld">
            <span className="md-label">Von</span>
            <input type="date" value={von} onChange={(e) => setVon(e.target.value)} />
          </label>
          <label className="md-feld">
            <span className="md-label">Bis (einschließlich)</span>
            <input type="date" value={bis} onChange={(e) => setBis(e.target.value)} />
          </label>
        </div>

        <div className="md-feld">
          <span className="md-label">Wer montiert</span>
          <div className="md-monteure">
            {monteure.map((m) => {
              const an = gewaehlt.includes(m.id);
              const belegt = konflikte.has(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  className={`md-monteur ${an ? 'aktiv' : ''}`}
                  title={belegt ? `${m.name} ist im Zeitraum bei ${konflikte.get(m.id)}` : m.name}
                  onClick={() => setGewaehlt((alt) => (an ? alt.filter((x) => x !== m.id) : [...alt, m.id]))}
                >
                  <span className="md-avatar">{m.kuerzel}</span>
                  {m.name}
                  {m.zusatz ? <span className="md-zusatz">· {m.zusatz}</span> : null}
                  {belegt && <span className="md-belegt" />}
                </button>
              );
            })}
            {monteure.length === 0 && (
              <span className="md-leer">Im Admin Center ist noch kein Monteur hinterlegt.</span>
            )}
          </div>
          {belegte.length > 0 && (
            <span className="md-konflikt">
              {belegte
                .map((id) => `${monteure.find((m) => m.id === id)?.name} ist zugleich bei ${konflikte.get(id)}`)
                .join(' · ')}
              {' — Speichern bleibt möglich.'}
            </span>
          )}
        </div>

        <label className="md-feld">
          <span className="md-label">Montageadresse</span>
          <input value={adresse} onChange={(e) => setAdresse(e.target.value)} />
          <span className="md-hinweis">
            aus dem Kunden übernommen · hier änderbar, wenn die Baustelle woanders ist
          </span>
        </label>

        {/* So steht es in Outlook (3.3) – Info-Slate, weil es Fremddaten beschreibt. */}
        <div className="md-vorschau">
          <span className="md-vorschau-kicker">So steht es in Outlook</span>
          <span className="md-vorschau-titel">{vorschauTitel || 'Montage'}</span>
          <span className="md-vorschau-zeit">
            {von && bis ? `${kurzDatum(von)}${von === bis ? '' : ` – ${kurzDatum(bis)}`}` : 'Zeitraum wählen'} ·
            ganztägig{adresse ? ` · Ort: ${adresse}` : ''}
          </span>
        </div>
      </div>
    </Modal>
  );
}
